/**
 * Wandelt einfaches Markdown (wie es KI-Entwürfe manchmal liefern) in HTML für den
 * Mail-Editor um. Unterstützt: Fett, Kursiv, Inline-Code, Links, Aufzählungen,
 * nummerierte Listen, Überschriften (als Fett) sowie Absätze/Zeilenumbrüche.
 *
 * Bereits vorhandenes HTML wird unverändert zurückgegeben (z. B. wenn der Server
 * schon HTML liefert), damit nichts doppelt escaped wird.
 */
import { EMAIL_BODY_P_MARGIN } from './emailHtmlFormat';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function applyInline(text: string): string {
  let out = escapeHtml(text);
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label: string, url: string) => `<a href="${url}">${label}</a>`,
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>');
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  return out;
}

function looksLikeHtml(input: string): boolean {
  return /<\/?(p|br|div|ul|ol|li|strong|em|b|i|a|h[1-6]|span|blockquote)\b/i.test(input);
}

/** Einzelne Zeilenumbrüche nach Satzende → Absatzgrenze (typisch bei KI-Plaintext). */
function normalizeParagraphBreaks(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/([.!?:…])\n(?=[A-ZÄÖÜ„"»])/g, '$1\n\n');
}

const LIST_STYLE = 'margin:0 0 0.75em 0;padding-left:1.25em';

export function markdownToEmailHtml(md: string): string {
  const raw = String(md || '');
  if (!raw.trim()) return '';
  if (looksLikeHtml(raw)) return raw;

  const lines = normalizeParagraphBreaks(raw).split('\n');
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let i = 0;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push(`<p style="${EMAIL_BODY_P_MARGIN}">${paragraph.map(applyInline).join('<br>')}</p>`);
      paragraph = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      flushParagraph();
      i += 1;
      continue;
    }

    if (/^[-*+]\s+/.test(trimmed)) {
      flushParagraph();
      const items: string[] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i].trim())) {
        items.push(`<li>${applyInline(lines[i].trim().replace(/^[-*+]\s+/, ''))}</li>`);
        i += 1;
      }
      blocks.push(`<ul style="${LIST_STYLE}">${items.join('')}</ul>`);
      continue;
    }

    if (/^\d+[.)]\s+/.test(trimmed)) {
      flushParagraph();
      const items: string[] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(`<li>${applyInline(lines[i].trim().replace(/^\d+[.)]\s+/, ''))}</li>`);
        i += 1;
      }
      blocks.push(`<ol style="${LIST_STYLE}">${items.join('')}</ol>`);
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushParagraph();
      blocks.push(`<p style="${EMAIL_BODY_P_MARGIN}"><strong>${applyInline(heading[2])}</strong></p>`);
      i += 1;
      continue;
    }

    paragraph.push(line);
    i += 1;
  }
  flushParagraph();

  return blocks.join('');
}
